import { toBigNum } from '@velocity-exchange/common';
import {
	parseInput,
	type DecimalStatus,
} from '@velocity-exchange/common/format';
import { BigNum, BN } from '@velocity-exchange/sdk';

export type InputBigNum =
	| { status: 'ok'; value: BigNum }
	| { status: Exclude<DecimalStatus, 'ok'>; value: null };

const exponentOf = (precisionExp: BN | number): number =>
	typeof precisionExp === 'number' ? precisionExp : precisionExp.toNumber();

/**
 * Reads a typed field into a BigNum at `precisionExp`, exactly.
 *
 * The parse itself is `parseInput`, which is fixed-point throughout, and
 * `toBigNum` carries the result across without a string or a float in between.
 *
 * `toBigNum` throws when a digit past `precisionExp` is non-zero, because it
 * never drops one silently. `parseInput` returns a value whose scale is already
 * `precisionExp`, so there is no digit past it to drop and the throw is
 * unreachable from here.
 *
 * A cleared or half-typed field is `nullish`, garbage is `invalid` and an
 * infinity is `non-finite`; none of the three carries a value, and the caller
 * decides what each one means.
 */
export function parseInputBigNum(
	input: string,
	precisionExp: BN | number
): InputBigNum {
	const exponent = exponentOf(precisionExp);
	const parsed = parseInput(input, exponent);

	if (parsed.status !== 'ok') {
		return { status: parsed.status, value: null };
	}

	return { status: 'ok', value: toBigNum(parsed.value, exponent) };
}

/**
 * The same parse with every non-numeric result read as zero, which is what
 * `BigNum.fromPrint` gave a cleared field.
 */
export function parseInputBigNumOrZero(
	input: string,
	precisionExp: BN | number
): BigNum {
	const parsed = parseInputBigNum(input, precisionExp);

	return parsed.value ?? BigNum.zero(new BN(exponentOf(precisionExp)));
}
